import { BadgeCheck, FilePlus2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatMoney } from '../../../../lib/formatMoney';
import type { BudgetPlanDetail } from '../budgetPlanTypes';
import {
  PPA_DEPARTMENT_PROGRESS,
  PPA_EXPENSE_CLASS_LABEL,
  type BudgetPlanDepartmentRow,
} from '../ppaTypes';
import { PpaModal } from './PpaModal';

/**
 * The plan's PPA surface — a DEPARTMENT ROSTER, and nothing beneath it.
 *
 * **One row per enrolled department, EVERY department**, whether or not it has proposed
 * anything. That is the reference's own shape and the reason the plan can say how many
 * offices have started out of how many there are: a budget officer opening a plan asks who
 * has submitted, not for every PPA across sixty offices, which is a flat list of hundreds of
 * rows with nothing to group them.
 *
 * **A department's own PPAs are a ROUTE, not a drill-down inside this panel.** It shipped as
 * a two-level panel on 21 Sep 2026 — press a row, the table is replaced in place, a Back
 * control above it — and that was rejected the same day: the second level had no URL, so it
 * could not be linked, bookmarked, opened in a tab, or returned to with the browser's own
 * Back. It was a navigation wearing a button, and the strip above it was a breadcrumb the
 * product already has a component for.
 *
 * **ONE PANEL, TWO SURFACES — the plan's PPA tab and Budget Review's department list.** The
 * reference draws both with one component over one DTO, and this is that shape kept: the
 * approved ceiling and the deviation are COLUMNS on the roster row rather than a second
 * record, and what tells the two surfaces apart is a callback rather than a flag. Budget
 * Review passes `onSetCeiling`; the plan's own tab passes nothing and never draws the entry.
 *
 * **The APPROVED BAND arrived with Budget Review**, which is what the tab shipped without on
 * 21 Sep 2026 and what this panel's own comment said was owed. The grouped header came with
 * it, for the reason it was withheld: one band over four columns says nothing the column
 * headers do not, and two bands is a line a reader has to be able to see.
 *
 * **The ROSTER is the page's**, not this panel's: the plan header counts started-out-of-
 * enrolled from the same rows, so fetching it twice would be two answers to one question.
 */
export function PpaRosterPanel({
  detail,
  roster,
  onChanged,
  onSetCeiling,
}: {
  detail: BudgetPlanDetail;
  roster: BudgetPlanDepartmentRow[];
  /** A write moves the plan's own header counts, so the PAGE refetches, not this panel. */
  onChanged: () => void;
  /**
   * Opens the approved-ceiling form for one department.
   *
   * **Its ABSENCE is what makes this the plan's tab rather than the reviewer's** — the
   * absent-callback idiom `<EditableAvatar>` already uses. The Budget Plan profile passes
   * nothing and the entry is not drawn at all; Budget Review passes it and the entry appears
   * behind `budget-review.set-ceiling`. One panel, two surfaces, and no flag to keep in step.
   */
  onSetCeiling?: (row: BudgetPlanDepartmentRow) => void;
}) {
  const { plan } = detail;
  const navigate = useNavigate();
  const canReadDepartments = usePermission('budget-departments.read');

  const [creatingFor, setCreatingFor] = useState<BudgetPlanDepartmentRow | null>(null);

  /*
    Searched, sorted and paged IN MEMORY. The rule against client-side filtering is about a
    PAGED list whose filter silently applies to the rows on screen and not the hundreds
    behind them; the roster arrives WHOLE with the record, so the filter sees everything
    there is — the same exemption a 201-file section takes.
  */
  const fetchRoster = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? roster.filter(
            (row) =>
              row.orgUnitName.toLowerCase().includes(term) ||
              (row.orgUnitCode ?? '').toLowerCase().includes(term),
          )
        : roster;

      const field = query.sort?.replace(/^-/, '') ?? 'orgUnitName';
      const descending = query.sort?.startsWith('-') ?? false;

      const sorted = [...matched].sort((a, b) => {
        /*
          UNPRICED SINKS whichever way the column points, so the ceiling is settled BEFORE the
          direction flip rather than inside `byField`. An em dash is not a zero: a department
          nobody has priced is not one approved at nothing, and sorting it among the figures
          would present it as the smallest ceiling on the plan. `PersonnelEligibility.Rating`
          takes the same call for the same reason.
        */
        if (field === 'approvedCeiling') {
          const left = a.approvedCeiling;
          const right = b.approvedCeiling;

          if (left === null || right === null) {
            return left === right ? 0 : left === null ? 1 : -1;
          }

          return descending ? right - left : left - right;
        }

        const byField =
          field === 'progress'
            ? a.progress - b.progress
            : field === 'ppaCount'
              ? a.ppaCount - b.ppaCount
              : field === 'totalItems'
                ? a.totalItems - b.totalItems
                : field === 'total'
                  ? a.total - b.total
                  : a.orgUnitName.localeCompare(b.orgUnitName);

        return descending ? -byField : byField;
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
        page: query.page,
        pageSize: query.pageSize,
      });
    },
    [roster],
  );

  const rosterState = useDataTable<BudgetPlanDepartmentRow>({
    fetchPage: fetchRoster,
    defaultSort: 'orgUnitName',
  });

  const lockedReason = plan.isEditable
    ? null
    : `This plan is ${plan.statusLabel.toLowerCase()}, so its PPAs can no longer be changed.`;

  const columns = useMemo<DataColumn<BudgetPlanDepartmentRow>[]>(
    () => [
      {
        key: 'orgUnitName',
        header: 'Department',
        width: 240,
        sortable: true,
        render: (row) => <TextCell value={row.orgUnitName} />,
      },
      {
        key: 'progress',
        header: 'Status',
        width: 130,
        sortable: true,
        summary: true,
        /*
          PLAIN state text through `<StatusText>`'s tones, and deliberately NOT `status: true`.
          That flag marks the ROW's own live-or-deleted state, which a roster row does not
          have — every row here is a live enrolment of a live unit. This is the case status,
          which is the distinction the disciplinary panel draws by carrying both columns.

          FOUR states and three usable tones, so the LABEL is what separates the two sharing
          one — On going and Budget finalization are both emerald, and the word says which.
          `pending` is the fourth tone and is refused: this panel renders `<IndexListView>`
          for its list view, which provides `ListSurfaceContext`, and amber is the brand
          colour rather than a fact about a record.
        */
        render: (row) =>
          row.progress === PPA_DEPARTMENT_PROGRESS.notStarted ? (
            <StatusText tone="inactive">{row.progressLabel}</StatusText>
          ) : row.progress === PPA_DEPARTMENT_PROGRESS.forReview ? (
            <StatusText tone="blocked">{row.progressLabel}</StatusText>
          ) : (
            <StatusText tone="active">{row.progressLabel}</StatusText>
          ),
      },
      {
        /*
          ONE column, `PPA items`, and it counts **PPAs** — not line items.

          It was TWO here, `PPAs` and `Line items`, on the reasoning that one PPA carrying
          twenty lines and twenty carrying one read identically on a PPA count alone. That
          reasoning is sound and it is not this table's to apply: the reference shows one
          column.

          **Collapsing them, this took the WRONG figure** — `totalItems`, on the strength of
          the header's word "items". The reference's own two screens settle it arithmetically:
          its department PPA list shows one PPA with `TOTAL ITEMS 10` totalling ₱305,900, and
          its roster shows that department with `PPA ITEMS 1` and the same ₱305,900. One PPA,
          ten lines, and the roster reads 1.

          So a column header is not evidence of what a column holds. `totalItems` is still on
          the row; nothing reads it here.
        */
        key: 'ppaCount',
        header: 'PPA items',
        width: 110,
        align: 'right',
        sortable: true,
        summary: true,
        render: (row) => <TextCell value={String(row.ppaCount)} mono numeric />,
      },
      /*
        TWO BANDS from here on. There is NO fund-source column, from 06 Oct 2026: the fund
        source IS the expense class, so the three class amounts below already say which ones
        this department is funding — a joined list of class names would print it twice.
      */
      {
        /*
          The FULL class names, which is what the reference prints — `PERSONNEL SERVIC…`
          truncated in its own header rather than abbreviated to `PS`. `MOOE` is already the
          form both maps carry. The short map was this table's own invention.
        */
        key: 'personnelServices',
        header: PPA_EXPENSE_CLASS_LABEL[0],
        group: 'Proposed budget',
        width: 180,
        align: 'right',
        render: (row) => <TextCell value={formatMoney(row.personnelServices)} mono numeric />,
      },
      {
        key: 'mooe',
        header: PPA_EXPENSE_CLASS_LABEL[1],
        group: 'Proposed budget',
        width: 130,
        align: 'right',
        render: (row) => <TextCell value={formatMoney(row.mooe)} mono numeric />,
      },
      {
        key: 'capitalOutlay',
        header: PPA_EXPENSE_CLASS_LABEL[2],
        group: 'Proposed budget',
        width: 150,
        align: 'right',
        render: (row) => <TextCell value={formatMoney(row.capitalOutlay)} mono numeric />,
      },
      {
        key: 'total',
        header: 'Total',
        group: 'Proposed budget',
        width: 150,
        align: 'right',
        sortable: true,
        render: (row) => <TextCell value={formatMoney(row.total)} mono numeric />,
      },
      {
        /*
          The BAND is `Ceiling` and the COLUMN is `Approved`, which is the reference's own
          naming. It shipped the other way round — band `Approved` over a column `Ceiling` —
          and a swap like that is invisible in review because both words are on screen and
          both belong to this table.
        */
        key: 'approvedCeiling',
        header: 'Approved',
        group: 'Ceiling',
        width: 150,
        align: 'right',
        sortable: true,
        /*
          An EM DASH where nothing is priced, never a zero. They are different facts: a zero
          is a reviewer approving nothing against this department, which is a decision
          somebody took, and `formatMoney` renders null as the dash for exactly that reason.
        */
        render: (row) => <TextCell value={formatMoney(row.approvedCeiling)} mono numeric />,
      },
      {
        key: 'deviation',
        header: 'Deviation',
        group: 'Ceiling',
        width: 150,
        align: 'right',
        /*
          Proposed less approved, derived SERVER-side over the two figures beside it — the
          SALN totals rule, which is also why the reference's three per-class ceiling columns
          "kept in sync on save" are absent from this suite entirely.

          Not sortable: the server's allow-list does not name it, and a header whose field the
          server cannot order by falls back to the default sort silently, which reads as a
          broken control.
        */
        render: (row) => <TextCell value={formatMoney(row.deviation)} mono numeric />,
      },
    ],
    [],
  );

  const rowActions = useCallback(
    (row: BudgetPlanDepartmentRow): MenuItem[] => [
      {
        key: `file-${row.budgetDepartmentId}`,
        label: 'File a PPA',
        icon: <FilePlus2 className="h-4 w-4" aria-hidden="true" />,
        // The key of what it DOES, never this panel's own.
        permission: 'ppas.create',
        disabledReason: lockedReason,
        onClick: () => setCreatingFor(row),
      },

      ...(onSetCeiling
        ? [
            {
              key: `ceiling-${row.budgetDepartmentId}`,
              label: 'Update approved ceiling',
              icon: <BadgeCheck className="h-4 w-4" aria-hidden="true" />,
              // The key of what it DOES. A reader of the plan's own tab never meets this
              // entry at all, because that surface passes no callback.
              permission: 'budget-review.set-ceiling',
              /*
                DISABLED for state, with the reason, rather than hidden: a department that
                proposed nothing has nothing to price and the server refuses it, so a reader
                expecting to price every row is told why one of them cannot be.
              */
              disabledReason:
                row.ppaCount === 0
                  ? 'This department has proposed nothing under this plan, so there is nothing to price.'
                  : null,
              onClick: () => onSetCeiling(row),
            } satisfies MenuItem,
          ]
        : []),
    ],
    [lockedReason, onSetCeiling],
  );

  /*
    The `p-4` is the CALLER's, because a `bleed` profile panel gives its child no inset —
    without it the search group's border sits on the panel's border and the two read as one
    control that has come apart. `<PositionProfilePage>` and `<PlantillaPositionProfilePage>`
    already did this; these two surfaces were the ones that did not.
  */
  return (
    <div className="flex h-full min-w-0 flex-col p-4">
      <DataTablePanel<BudgetPlanDepartmentRow>
        id="ppa-roster"
        state={rosterState}
        columns={columns}
        rowKey={(row) => row.budgetDepartmentId}
        searchPlaceholder="Search departments"
        fill
        /*
          A ROSTER ROW IS AN ENROLLED DEPARTMENT, so an empty enrolment list is a plan nobody
          can file a PPA against — there is no row to file one under. That is the module
          working, and it read as the module being broken: the sentence named Settings in prose
          and left the reader to find it.

          Naming the control in prose is the tell that the affordance is missing — the
          `<ProfileCard>` empty-state rule, one surface along — so the way out is a LINK, and
          `<EmptyState>`'s message is a `ReactNode` for exactly this.

          The link is dropped without `budget-departments.read` and the requirement is still
          named: a reader who cannot open that page still has to know what is in the way, and
          sending them somewhere that would bounce them is worse than telling them who to ask.
        */
        emptyMessage={
          <>
            No budget departments are enrolled, so this plan has no roster — a PPA is filed
            against a department, and there is none to file one under yet.{' '}
            {canReadDepartments ? (
              <Link
                to="/procurement/investment-planning/settings/budget-departments"
                className="font-medium text-slate-800 hover:underline"
              >
                Enrol the offices that propose a budget
              </Link>
            ) : (
              'Somebody with access to Budget Departments has to enrol the offices that propose a budget'
            )}
            .
          </>
        }
        emptySearchMessage="No department matches that search."
        /*
          NO Other Info and NO creation columns. Every figure on this row is computed at read
          time from the enrolment and the PPAs beneath it, so there is no creator to name, no
          creation instant and no update trail — the popover would be four em dashes, which
          is the dead affordance the hide rule forbids. Stock Levels takes the same exemption
          for the same reason.
        */
        otherInfo={() => null}
        auditColumns={false}
        selectable={false}
        rowActions={rowActions}
        /*
          The anchor NAVIGATES. `budget-plans.read` is the key of what it opens — this
          department's slice of the plan, which is still the plan — and a reader without it
          is not on this tab to begin with.
        */
        onRowOpen={(row) =>
          navigate(
            `/procurement/investment-planning/budget-plans/${plan.id}/departments/${row.orgUnitId}`,
          )
        }
        openPermission="budget-plans.read"
      />

      {creatingFor ? (
        <PpaModal
          open
          budgetPlanId={plan.id}
          planNumber={plan.planNumber}
          orgUnitId={creatingFor.orgUnitId}
          orgUnitName={creatingFor.orgUnitName}
          onClose={() => setCreatingFor(null)}
          onSaved={() => {
            setCreatingFor(null);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}
